'use client';

import { useState, useMemo, useEffect, useRef } from 'react';
import { format, parse, setHours, setMinutes } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { FIELD_SURFACE } from "@/components/ui/field-surface";
interface DateTimePickerProps {
  value?: string; // ISO string or datetime-local format (YYYY-MM-DDTHH:mm)
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  minDate?: Date;
  showTime?: boolean; // If false, shows date-only picker
}

// Generate time slots from 00:00 to 23:30 in 30-minute intervals
function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      slots.push(
        `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
      );
    }
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
      <line x1="16" x2="16" y1="2" y2="6" />
      <line x1="8" x2="8" y1="2" y2="6" />
      <line x1="3" x2="21" y1="10" y2="10" />
    </svg>
  );
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = 'Pick date & time',
  className,
  disabled = false,
  minDate,
  showTime = true,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);

  // Parse the value into date and time parts
  const { date, time } = useMemo(() => {
    if (!value) return { date: undefined, time: undefined };
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      const parsed = parse(value, "yyyy-MM-dd'T'HH:mm", new Date());
      if (isNaN(parsed.getTime())) return { date: undefined, time: undefined };
      return { date: parsed, time: format(parsed, 'HH:mm') };
    }
    return { date: d, time: format(d, 'HH:mm') };
  }, [value]);

  // Handle date selection
  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) return;

    if (!showTime) {
      // For date-only mode, use midnight and format as date only
      const formatted = format(selectedDate, "yyyy-MM-dd'T'00:00");
      onChange?.(formatted);
      setOpen(false); // Close immediately for date-only
      return;
    }

    // Combine with existing time or default to 00:00
    const [hours, minutes] = (time || '00:00').split(':').map(Number);
    const combined = setMinutes(setHours(selectedDate, hours), minutes);
    const formatted = format(combined, "yyyy-MM-dd'T'HH:mm");
    onChange?.(formatted);
  };

  // Handle time selection
  const handleTimeSelect = (selectedTime: string) => {
    // Use current date or today
    const baseDate = date || new Date();
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const combined = setMinutes(setHours(baseDate, hours), minutes);
    const formatted = format(combined, "yyyy-MM-dd'T'HH:mm");
    onChange?.(formatted);
  };

  // Format display value
  const displayValue = useMemo(() => {
    if (!date) return null;
    const dateStr = format(date, 'PPP'); // "Dec 23, 2025"
    if (!showTime) return dateStr; // Date only
    const timeStr = time || '00:00';
    return `${dateStr} - ${timeStr}`;
  }, [date, time, showTime]);

  // Scroll to selected time when popover opens
  const timeScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open || !showTime || !time) return;

    const rafId = requestAnimationFrame(() => {
      const container = timeScrollRef.current;
      const el = container?.querySelector(`[data-time="${time}"]`) as HTMLElement | null;
      if (!container || !el) return;
      container.scrollTop = Math.max(0, el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2);
    });
    return () => cancelAnimationFrame(rafId);
  }, [open, showTime, time]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button          variant="outline"
          disabled={disabled}
          className={cn(
            cn(FIELD_SURFACE, 'h-11 w-full justify-between font-inter text-sm text-left font-normal disabled:shadow-none'),
            !displayValue && 'text-grey-light',
            className
          )}
        >
          <span>{displayValue || placeholder}</span>
          <CalendarIcon className="size-4 shrink-0 text-grey" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex max-sm:flex-col">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            defaultMonth={date}
            disabled={minDate ? [{ before: minDate }] : undefined}
            autoFocus
          />
          {showTime && (
            <div className="w-40 max-sm:border-t sm:border-l border-dinnerplate-white">
              <div className="flex h-10 shrink-0 items-center px-4 border-b border-dinnerplate-white">
                <p className="text-sm font-medium font-funkis text-grey">
                  {date ? format(date, 'EEEE, d') : 'Pick a date'}
                </p>
              </div>
              <div
                ref={timeScrollRef}
                className="h-[280px] overflow-y-auto"
              >
                <div className="grid gap-1.5 p-3">
                  {TIME_SLOTS.map((slot) => (
                    <Button
                      key={slot}
                      variant={time === slot ? 'primary' : 'outline'}
                      size="sm"
                      data-time={slot}
                      onClick={() => handleTimeSelect(slot)}
                      className={cn(
                        'w-full justify-center font-funkis',
                        time === slot && 'bg-brand-yellow text-off-black'
                      )}
                    >
                      {slot}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
